<template>
  <div ref="trailContainer" class="trail-map-container relative overflow-y-auto overflow-x-hidden">
    <!-- Full-width background -->
    <div class="trail-bg absolute inset-0 pointer-events-none" />

    <div
      class="trail-map-inner relative"
      :style="{ height: totalHeight + 'px' }"
    >
      <!-- Summit decoration -->
      <div class="absolute -translate-x-1/2" :style="{ left: lastNodePercent + '%', top: (lastNodeY - 90) + 'px' }">
        <svg width="160" height="90" viewBox="0 0 160 90" fill="none">
          <!-- Mountain range backdrop -->
          <polygon points="30,80 80,10 130,80" fill="#94a3b8" opacity="0.3" />
          <polygon points="0,80 50,25 100,80" fill="#cbd5e1" opacity="0.25" />
          <polygon points="60,80 110,20 160,80" fill="#94a3b8" opacity="0.2" />
          <!-- Main peak -->
          <polygon points="80,10 50,70 110,70" fill="#94a3b8" opacity="0.5" />
          <polygon points="80,10 60,45 100,45" fill="#cbd5e1" opacity="0.6" />
          <!-- Snow cap -->
          <polygon points="80,10 68,28 92,28" fill="white" opacity="0.9" />
          <!-- Flag -->
          <line x1="80" y1="8" x2="80" y2="-4" stroke="#dc2626" stroke-width="2" />
          <polygon points="80,-4 98,0 80,4" fill="#dc2626" />
          <!-- SUMMIT text -->
          <text x="80" y="82" text-anchor="middle" font-size="11" font-weight="bold" fill="#64748b">SUMMIT</text>
        </svg>
      </div>

      <!-- SVG trail path layer (full width, nodes positioned using offsets) -->
      <svg
        class="absolute inset-0 w-full h-full pointer-events-none"
        :viewBox="`0 0 ${containerWidth} ${totalHeight}`"
      >
        <!-- Tree decorations scattered along the trail -->
        <template v-for="tree in trees" :key="'tree-' + tree.x + tree.y">
          <g :transform="`translate(${tree.x}, ${tree.y}) scale(${tree.scale})`">
            <polygon points="0,-18 -8,0 8,0" :fill="tree.color" opacity="0.6" />
            <polygon points="0,-12 -6,2 6,2" :fill="tree.color" opacity="0.8" />
            <rect x="-1.5" y="0" width="3" height="6" fill="#92400e" rx="1" />
          </g>
        </template>

        <!-- Cloud decorations near the top -->
        <template v-for="cloud in clouds" :key="'cloud-' + cloud.x + cloud.y">
          <g :transform="`translate(${cloud.x}, ${cloud.y})`" opacity="0.3">
            <ellipse cx="0" cy="0" rx="24" ry="9" fill="white" />
            <ellipse cx="16" cy="-4" rx="18" ry="7" fill="white" />
            <ellipse cx="-12" cy="-2" rx="15" ry="6" fill="white" />
          </g>
        </template>

        <!-- Trail shadow (full path) -->
        <path
          v-if="trailPath"
          :d="trailPath"
          stroke="#d4c4a8"
          :stroke-width="16"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <!-- Trail main (full path) -->
        <path
          v-if="trailPath"
          :d="trailPath"
          stroke="#c2a878"
          :stroke-width="10"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <!-- Trail completed portion -->
        <path
          v-if="completedPath"
          :d="completedPath"
          stroke="#4ade80"
          :stroke-width="10"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="trail-completed-line"
          :style="{ strokeDasharray: completedPathLength, strokeDashoffset: animatedOffset }"
        />
      </svg>

      <!-- Campsite markers at unit boundaries -->
      <TrailCampsite
        v-for="camp in campsitePositions"
        :key="'camp-' + camp.unitId"
        :title="camp.title"
        :style="{
          position: 'absolute',
          left: camp.xPercent + '%',
          transform: 'translateX(-50%)',
          top: camp.y + 'px'
        }"
      />

      <!-- Lesson nodes -->
      <div
        v-for="(node, idx) in nodePositions"
        :key="node.lesson.id"
        :style="{
          position: 'absolute',
          left: node.xPercent + '%',
          top: node.y + 'px',
          transform: 'translate(-50%, -50%)'
        }"
      >
        <TrailNode
          :lesson="node.lesson"
          :index="idx"
          @select="$emit('select-lesson', $event)"
        />
      </div>

      <!-- Avatar at current lesson -->
      <TrailAvatar
        v-if="currentNodePosition"
        :x="currentNodePosition.xPx"
        :y="currentNodePosition.y"
      />

      <!-- Ground / Start decoration -->
      <div
        class="absolute -translate-x-1/2"
        :style="{ left: firstNodePercent + '%', top: (firstNodeY + 40) + 'px' }"
      >
        <div class="flex flex-col items-center">
          <svg width="200" height="40" viewBox="0 0 200 40" fill="none">
            <!-- Grass -->
            <path d="M0,20 Q50,5 100,20 Q150,35 200,20 L200,40 L0,40 Z" fill="#4ade80" opacity="0.3" />
            <path d="M0,25 Q50,15 100,25 Q150,35 200,25 L200,40 L0,40 Z" fill="#22c55e" opacity="0.2" />
          </svg>
          <div class="px-5 py-1.5 rounded-full bg-green-100 text-green-800 text-sm font-bold -mt-3">
            START
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted, nextTick } from 'vue';
import TrailNode from './TrailNode.vue';
import TrailCampsite from './TrailCampsite.vue';
import TrailAvatar from './TrailAvatar.vue';

const props = defineProps({
  units: { type: Array, required: true }
});

defineEmits(['select-lesson']);

const trailContainer = ref(null);
const animatedOffset = ref('999');
const containerWidth = ref(800);

// Node positions as percentages of container width
const leftPercent = ref(20);
const rightPercent = ref(80);

function updateDimensions() {
  const w = window.innerWidth;
  containerWidth.value = w;
  if (w < 640) {
    leftPercent.value = 18;
    rightPercent.value = 82;
  } else if (w < 1024) {
    leftPercent.value = 22;
    rightPercent.value = 78;
  } else {
    leftPercent.value = 25;
    rightPercent.value = 75;
  }
}

const NODE_SPACING_Y = 110;
const CAMPSITE_HEIGHT = 70;
const TOP_PADDING = 120;
const BOTTOM_PADDING = 80;

// Compute all positions
const nodePositions = computed(() => {
  const positions = [];
  let globalIndex = 0;

  for (const unit of props.units) {
    for (let i = 0; i < unit.lessons.length; i++) {
      const GROUP_SIZE = 3;
      const groupIndex = Math.floor(globalIndex / GROUP_SIZE);
      const posInGroup = globalIndex % GROUP_SIZE;
      const isLeft = groupIndex % 2 === 0;
      const basePct = isLeft ? leftPercent.value : rightPercent.value;
      const spreadDir = isLeft ? 1 : -1;
      const pct = basePct + posInGroup * 4 * spreadDir;
      positions.push({
        lesson: unit.lessons[i],
        unitId: unit.id,
        globalIndex,
        xPercent: pct,
        // Absolute pixel x for SVG path and avatar
        xPx: (pct / 100) * containerWidth.value,
        isFirstInUnit: i === 0,
        isLastInUnit: i === unit.lessons.length - 1
      });
      globalIndex++;
    }
  }

  // Total height, invert Y (bottom = start)
  const unitCount = props.units.length;
  const rawTotalHeight = positions.length * NODE_SPACING_Y + unitCount * CAMPSITE_HEIGHT + TOP_PADDING + BOTTOM_PADDING;

  let currentY = rawTotalHeight - BOTTOM_PADDING;
  let unitIdx = 0;
  let lessonInUnit = 0;

  for (let i = 0; i < positions.length; i++) {
    positions[i].y = currentY;
    currentY -= NODE_SPACING_Y;

    lessonInUnit++;
    if (positions[i].isLastInUnit && unitIdx < unitCount - 1) {
      currentY -= CAMPSITE_HEIGHT;
      unitIdx++;
      lessonInUnit = 0;
    }
  }

  return positions;
});

// Campsite positions
const campsitePositions = computed(() => {
  const camps = [];
  for (let i = 0; i < nodePositions.value.length; i++) {
    const node = nodePositions.value[i];
    if (node.isLastInUnit && i < nodePositions.value.length - 1) {
      const nextNode = nodePositions.value[i + 1];
      camps.push({
        unitId: nextNode.unitId,
        title: props.units.find(u => u.id === nextNode.unitId)?.title || '',
        y: (node.y + nextNode.y) / 2 - 15,
        xPercent: (node.xPercent + nextNode.xPercent) / 2
      });
    }
  }
  return camps;
});

// Total map height
const totalHeight = computed(() => {
  if (!nodePositions.value.length) return 400;
  const unitCount = props.units.length;
  return nodePositions.value.length * NODE_SPACING_Y + unitCount * CAMPSITE_HEIGHT + TOP_PADDING + BOTTOM_PADDING;
});

// First/last node positions for start/end decorations
const firstNodePercent = computed(() => nodePositions.value[0]?.xPercent || 50);
const firstNodeY = computed(() => nodePositions.value[0]?.y || totalHeight.value - BOTTOM_PADDING);
const lastNodePercent = computed(() => nodePositions.value[nodePositions.value.length - 1]?.xPercent || 50);
const lastNodeY = computed(() => nodePositions.value[nodePositions.value.length - 1]?.y || TOP_PADDING);

// Current node position for avatar
const currentNodePosition = computed(() => {
  return nodePositions.value.find(n => n.lesson.status === 'current') || null;
});

// SVG path through all nodes (uses pixel coords based on containerWidth)
const trailPath = computed(() => {
  const nodes = nodePositions.value;
  if (nodes.length < 2) return '';

  let d = `M ${nodes[0].xPx} ${nodes[0].y}`;
  for (let i = 1; i < nodes.length; i++) {
    const prev = nodes[i - 1];
    const curr = nodes[i];
    const midY = (prev.y + curr.y) / 2;
    d += ` C ${prev.xPx} ${midY}, ${curr.xPx} ${midY}, ${curr.xPx} ${curr.y}`;
  }
  return d;
});

// Completed portion
const completedPath = computed(() => {
  const nodes = nodePositions.value;
  // Extend solid line to include the current lesson (not just completed)
  const lastActive = nodes.reduce((last, n, i) => (n.lesson.status === 'completed' || n.lesson.status === 'current') ? i : last, -1);
  if (lastActive < 0) return '';
  if (lastActive === 0 && nodes.length > 1) {
    return `M ${nodes[0].xPx} ${nodes[0].y} L ${nodes[0].xPx} ${nodes[0].y}`;
  }

  const end = lastActive + 1;
  let d = `M ${nodes[0].xPx} ${nodes[0].y}`;
  for (let i = 1; i < end; i++) {
    const prev = nodes[i - 1];
    const curr = nodes[i];
    const midY = (prev.y + curr.y) / 2;
    d += ` C ${prev.xPx} ${midY}, ${curr.xPx} ${midY}, ${curr.xPx} ${curr.y}`;
  }
  return d;
});

// Path length for animation
const completedPathLength = computed(() => {
  const nodes = nodePositions.value;
  const lastActive = nodes.reduce((last, n, i) => (n.lesson.status === 'completed' || n.lesson.status === 'current') ? i : last, -1);
  if (lastActive < 1) return '0';
  let len = 0;
  for (let i = 1; i <= lastActive; i++) {
    const dx = nodes[i].xPx - nodes[i - 1].xPx;
    const dy = nodes[i].y - nodes[i - 1].y;
    len += Math.sqrt(dx * dx + dy * dy) * 1.3;
  }
  return String(Math.round(len));
});

// Tree decorations
const trees = computed(() => {
  const result = [];
  const nodes = nodePositions.value;
  const w = containerWidth.value;

  for (let i = 0; i < nodes.length; i++) {
    const seed = i * 7 + 3;
    const side = nodes[i].xPx < w / 2 ? 'right' : 'left';
    const baseX = side === 'right' ? w * 0.88 - (seed % 40) : w * 0.12 + (seed % 40);
    const yOffset = -20 + (seed % 40);

    if (i % 2 === 0) {
      result.push({
        x: baseX,
        y: nodes[i].y + yOffset,
        scale: 0.7 + (seed % 4) * 0.1,
        color: ['#16a34a', '#15803d', '#22c55e'][seed % 3]
      });
    }
  }
  return result;
});

// Cloud decorations near the top
const clouds = computed(() => {
  if (totalHeight.value < 400) return [];
  const w = containerWidth.value;
  return [
    { x: w * 0.1, y: 50 },
    { x: w * 0.85, y: 100 },
    { x: w * 0.15, y: 180 },
    { x: w * 0.9, y: 250 }
  ];
});

onMounted(() => {
  updateDimensions();
  window.addEventListener('resize', updateDimensions);

  nextTick(() => {
    animatedOffset.value = completedPathLength.value;
    requestAnimationFrame(() => {
      animatedOffset.value = '0';
    });
  });

  nextTick(() => {
    setTimeout(() => {
      const currentEl = trailContainer.value?.querySelector('[data-lesson-status="current"]');
      if (currentEl) {
        currentEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
  });
});

onUnmounted(() => {
  window.removeEventListener('resize', updateDimensions);
});
</script>

<style scoped>
.trail-map-container {
  max-height: calc(100vh - 140px);
  scrollbar-width: thin;
}

.trail-map-container::-webkit-scrollbar {
  width: 6px;
}

.trail-map-container::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.trail-bg {
  background: linear-gradient(
    to bottom,
    #e0f2fe 0%,
    #f0f9ff 20%,
    #fefce8 60%,
    #dcfce7 85%,
    #bbf7d0 100%
  );
}

.trail-completed-line {
  transition: stroke-dashoffset 1.5s ease-out;
}
</style>
